import { Modal, Select, Spin, Image, Checkbox, Space } from "antd"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { BomModelConfigurationPointEitDTO, BomModelConfigurationPointViewDTO, BomModelPlacementPointDTO, BomModelService, BomModelSurfaceDTO, MaterialService, MaterialViewDTO, ModelService, ModelViewDTO } from "../../backendapi/requests";
import { useDebouncedCallback } from 'use-debounce';
import { ConfigContext } from "../../contexts/ConfigContext";
import { GenerateODataFilter } from "../../helpers/odataFunctions";
import { FilterGroup } from "../../types/types";
import { BASE_PATH } from "../../Environment";

const BomModelDesigner: React.FC<{ modelId: number, isOpen: boolean, onSave: (configuration: string, placements: BomModelPlacementPointDTO[], surfaces: BomModelSurfaceDTO[]) => void, onClose: () => void }> = ({ modelId, isOpen, onClose, onSave }) => {
    const [configuration, setConfiguration] = useState("");
    const [placementPoints, setPlacementPoints] = useState<BomModelPlacementPointDTO[]>([]);
    const [surfaces, setSurfaces] = useState<BomModelSurfaceDTO[]>([]);
    const [points, setPoints] = useState<BomModelConfigurationPointViewDTO[]>([]);

    const handleSave = useCallback(() => {
        if (configuration != "") {
            onSave(configuration, placementPoints, surfaces);
        }
    }, [configuration, placementPoints, surfaces]);

    useEffect(() => {
        if (isOpen) {
            setConfiguration("");
            setPlacementPoints([]);
            setSurfaces([]);
            setPoints([]);

            BomModelService.putBackendv2BommodelConfiguration(modelId, []).then(state => {
                setConfiguration(state.configuration);
                setPlacementPoints(state.placementpoints);
                setSurfaces(state.surfaces);
                setPoints(state.points);
            });
        }
    }, [modelId, isOpen]);

    const handlePointChange = useCallback((point: BomModelConfigurationPointViewDTO, isExternal: boolean, value: string | undefined) => {
        var newPoints: BomModelConfigurationPointEitDTO[] = points.map(p => ({ type: p.type, name: p.name, path: p.path, isExternal: p.isExternal, value: p.asset?.key })).filter(e => e.name != point.name || e.path != point.path);
        newPoints.push({ type: point.type, name: point.name, path: point.path, isExternal: isExternal, value: value });

        BomModelService.putBackendv2BommodelConfiguration(modelId, newPoints).then(state => {
            setConfiguration(state.configuration);
            setPlacementPoints(state.placementpoints);
            setSurfaces(state.surfaces);
            setPoints(state.points);
        });
    }, [points]);

    return <Modal title="Edit configuration" open={isOpen} width={1000} onCancel={onClose} onOk={handleSave}>
        {points.filter(e => e.path == "").map(p => <BomModelDesignerPoint key={p.path + p.name} point={p} points={points} onChange={handlePointChange} />)}
    </Modal>
}

type Option = {
    label: string;
    value: string;
    item: ModelViewDTO | MaterialViewDTO | undefined;
}

const BomModelDesignerPoint: React.FC<{ point: BomModelConfigurationPointViewDTO, points: BomModelConfigurationPointViewDTO[], onChange: (point: BomModelConfigurationPointViewDTO, isExternal: boolean, value: string) => void }> = ({ point, points, onChange }) => {
    const { currentClient } = useContext(ConfigContext);

    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<Option[]>([]);
    const fetchRef = useRef("-");

    let value: Option | undefined = point.asset == null ? undefined : {
        label: point.asset.title,
        value: point.asset.key,
        item: undefined
    };


    const debounceFetcher = useDebouncedCallback((value: string) => {
        if (value == fetchRef.current) {
            return;
        }

        const fetchId = value || "";
        fetchRef.current = fetchId;
        setOptions([]);
        setFetching(true);

        const filter: FilterGroup = {
            type: 'group', filters: [
                { type: 'search', values: [{ value: value }], property: 'title', name: 'title' },
                { type: 'select', values: [{ value: value }], property: 'metadata.article_number,metadata.ean', name: 'metadata' }
            ], join: 'or'
        };

        if (point.type == 'model') {
            ModelService.getBackendv2Model(currentClient.id, point.filter, '25', undefined, GenerateODataFilter([filter])).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }

                setOptions(newOptions.value.map(e => ({ item: e, label: e.title, value: e.name })));
                setFetching(false);
            });
        }

        if (point.type == 'surface') {
            MaterialService.getBackendv2Material(currentClient.id, point.filter, '25', undefined, GenerateODataFilter([filter])).then((newOptions) => {
                if (fetchId !== fetchRef.current) {
                    return;
                }

                setOptions(newOptions.value.map(e => ({ item: e, label: e.title, value: e.name })));
                setFetching(false);
            });
        }
    }, 500);

    const handleOpen = useCallback((isOpen: boolean) => {
        if (isOpen) {
            debounceFetcher("");
        }
    }, [debounceFetcher]);

    const handleChange = useCallback((item: Option) => {
        onChange(point, point.isExternal, item as unknown as string);
    }, [onChange]);

    const handleToggle = useCallback(() => {
        onChange(point, !point.isExternal, point.asset?.key);
    }, [onChange]);

    return <div>
        <h4 style={{ marginBottom: '0' }}>{point.label}</h4>
        <Space>
            <Select
                showSearch={true}
                onSearch={debounceFetcher}
                onSelect={handleChange}
                onOpenChange={handleOpen}
                filterOption={false}
                notFoundContent={fetching ? <Spin size="small" /> : 'No results found'}
                options={options}
                style={{ width: '500px' }}
                value={value}
                optionRender={(option) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {option.data.item && <Image
                            preview={false}
                            src={BASE_PATH + option.data.item.thumbnail}
                            height={50}
                        />}
                        {option.label}
                    </div>
                )}
            />
            <Checkbox checked={point.isExternal} onChange={handleToggle} >External</Checkbox>
        </Space>

        <div style={{ paddingLeft: '28px' }}>
            {points.filter(e => e.path == (point.path == "" ? point.name : point.path + ";" + point.name)).map(p => <BomModelDesignerPoint key={p.path + p.name} point={p} points={points} onChange={onChange} />)}
        </div>
    </div>
}


export default BomModelDesigner;